import { useState, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOLOGIAS = [
  { value: "T0 · RC · Jardim 29–60m²", label: "T0 · Rés-do-Chão · Jardim 29–60m² (47,50m²)" },
  { value: "T1 · RC · Jardim 50–77m²", label: "T1 · Rés-do-Chão · Jardim 50–77m² (66,50m²)" },
  { value: "T1 · 1º Piso · Varanda", label: "T1 · 1º Piso · Varanda 17–24m² (66,50m²)" },
  { value: "T1 · Sótão · Mansarda", label: "T1 · Sótão · Mansarda (65m²)" },
  { value: "T2 · 1º Piso · Varanda 22m²", label: "T2 · 1º Piso · Varanda 22m² · Suíte (89m²)" },
  { value: "T2 · Sótão · Terraço 30m²", label: "T2 · Sótão · Terraço 30m² · Suíte (82m²)" },
  { value: "T3 · RC · Jardim 130–220m²", label: "T3 · Rés-do-Chão · Jardim 130–220m² (132m²)" },
  { value: "T3 · 1º Piso · Varanda", label: "T3 · 1º Piso · Varanda 14–28m² · Suíte (131m²)" },
  { value: "T3 · Sótão · Mansarda", label: "T3 · Sótão · Terraço · Suíte (131m²)" },
  { value: "T4 · RC · 2 Suítes · Premium", label: "T4 · Rés-do-Chão · 2 Suítes · Jardim 155–240m² (155m²)" },
  { value: "Indiferente / Investimento", label: "Indiferente · Investimento" },
];

const BLOCOS = [
  { value: "A", label: "Bloco A" },
  { value: "B", label: "Bloco B" },
  { value: "C", label: "Bloco C" },
  { value: "Indiferente", label: "Indiferente" },
];

const ContactForm = forwardRef(function ContactForm(_, ref) {
  const [form, setForm] = useState({
    nome: "",
    telefone: "",
    email: "",
    tipologia: "",
    bloco: "",
    agencia: "",
    mensagem: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useImperativeHandle(ref, () => ({
    setTipologia: (code, pisoId) => {
      const map = {
        rc: {
          T0: "T0 · RC · Jardim 29–60m²",
          T1: "T1 · RC · Jardim 50–77m²",
          T3: "T3 · RC · Jardim 130–220m²",
          T4: "T4 · RC · 2 Suítes · Premium",
        },
        p1: {
          T1: "T1 · 1º Piso · Varanda",
          T2: "T2 · 1º Piso · Varanda 22m²",
          T3: "T3 · 1º Piso · Varanda",
        },
        sotao: {
          T1: "T1 · Sótão · Mansarda",
          T2: "T2 · Sótão · Terraço 30m²",
          T3: "T3 · Sótão · Mansarda",
        },
      };
      const value = (map[pisoId] && map[pisoId][code]) || map.rc[code] || "";
      setForm((f) => ({ ...f, tipologia: value }));
    },
  }));

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.telefone.trim() || !form.email.trim()) {
      toast.error("Por favor preencha nome, telefone e email.");
      return;
    }
    if (!form.agencia) {
      toast.error("Por favor selecione a agência (ERA ou RE/MAX).");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        telefone: form.telefone.trim(),
        email: form.email.trim(),
        tipologia: form.tipologia || null,
        bloco: form.bloco || null,
        agencia: form.agencia,
        mensagem: form.mensagem.trim() || null,
        origem: "landing-page",
      };
      await axios.post(`${API}/leads`, payload);
      setSubmitted(true);
      toast.success("Pedido recebido. Entraremos em contacto em menos de 24h.");
      setForm({ nome: "", telefone: "", email: "", tipologia: "", bloco: "", agencia: "", mensagem: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Não foi possível enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-24 lg:py-36 bg-[#F9F9F6]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left column */}
          <div className="lg:col-span-5 reveal">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#5C5C59] mb-6">Contacto</div>
            <h2 className="font-display text-[36px] sm:text-[44px] lg:text-[56px] leading-[1.05] tracking-[-0.02em] text-[#1A1A1A] text-balance">
              Pronto para
              <br />
              <span className="italic-display text-[#2B3B31]">dar o próximo passo?</span>
            </h2>
            <p className="mt-8 text-[15px] leading-relaxed text-[#5C5C59] font-light max-w-md">
              Fale com a nossa equipa de vendas e receba toda a informação sobre tipologias, preços e condições de reserva. Sem compromisso.
            </p>

            <blockquote className="mt-10 pl-5 border-l border-[#2B3B31]/30 text-[15px] text-[#1A1A1A]/80 italic leading-relaxed max-w-md">
              "Cada detalhe do ANREÁL Residence reflete o compromisso de uma equipa que eleva o conceito de viver com exclusividade e sofisticação."
            </blockquote>

            <a
              href="mailto:geral@diamondinvest.pt"
              data-testid="contact-email"
              className="mt-10 inline-flex items-center gap-3 text-[#1A1A1A] hover:text-[#2B3B31] transition-colors"
            >
              <Mail size={18} strokeWidth={1.25} />
              <span className="font-display text-lg lg:text-xl tracking-tight">geral@diamondinvest.pt</span>
            </a>

            <div className="mt-12 pt-8 border-t border-[#E5E5DF] grid grid-cols-2 gap-6 max-w-md">
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#9E9789] mb-2">Resposta</div>
                <div className="text-[13px] text-[#1A1A1A]">Menos de 24h</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#9E9789] mb-2">Fase</div>
                <div className="text-[13px] text-[#1A1A1A]">Lançamento ativo</div>
              </div>
            </div>
          </div>

          {/* Right column - form */}
          <div className="lg:col-span-6 lg:col-start-7 reveal" style={{ transitionDelay: "120ms" }}>
            <div className="bg-white border border-[#E5E5DF] p-8 lg:p-12">
              <div className="flex items-center justify-between mb-8">
                <div className="text-[11px] tracking-[0.28em] uppercase text-[#2B3B31]">Agende a sua visita</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#9E9789]">Disponibilidade Limitada</div>
              </div>

              {submitted ? (
                <div data-testid="contact-success" className="py-10">
                  <div className="font-display text-2xl lg:text-3xl text-[#2B3B31] mb-3">Obrigado.</div>
                  <p className="text-[15px] text-[#5C5C59] leading-relaxed">
                    Recebemos o seu pedido. A nossa equipa entrará em contacto em menos de 24 horas com toda a informação sobre o ANREÁL Residence.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-new-submit"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-[11px] tracking-[0.22em] uppercase text-[#2B3B31] border-b border-[#2B3B31]/40 hover:border-[#2B3B31] pb-0.5"
                  >
                    Enviar Novo Pedido
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-7">
                  <Field
                    id="nome"
                    label="Nome Completo"
                    value={form.nome}
                    onChange={(v) => update("nome", v)}
                    placeholder="O seu nome"
                    testId="contact-input-name"
                    required
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    <Field
                      id="telefone"
                      label="Telefone"
                      type="tel"
                      value={form.telefone}
                      onChange={(v) => update("telefone", v)}
                      placeholder="+351 …"
                      testId="contact-input-phone"
                      required
                    />
                    <Field
                      id="email"
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={(v) => update("email", v)}
                      placeholder="exemplo@email.pt"
                      testId="contact-input-email"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] tracking-[0.22em] uppercase text-[#5C5C59] mb-3">
                      Agência <span className="text-[#C0392B]">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Agência">
                      {[
                        { v: "ERA", label: "ERA" },
                        { v: "REMAX", label: "RE/MAX" },
                      ].map((a) => {
                        const active = form.agencia === a.v;
                        return (
                          <button
                            key={a.v}
                            type="button"
                            data-testid={`contact-agency-${a.v}`}
                            aria-pressed={active}
                            onClick={() => update("agencia", a.v)}
                            className={`text-center px-5 py-4 border transition-all duration-300 ${
                              active
                                ? "bg-[#2B3B31] border-[#2B3B31] text-[#F9F9F6]"
                                : "bg-white border-[#E5E5DF] text-[#1A1A1A] hover:border-[#2B3B31]"
                            }`}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${active ? "bg-[#F9F9F6]" : "bg-[#9E9789]"}`} />
                              <span className="font-display text-base tracking-tight">{a.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[10px] text-[#9E9789]">
                      A agência selecionada irá acompanhar o seu pedido e entrar em contacto consigo.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    <div>
                      <label className="block text-[10px] tracking-[0.22em] uppercase text-[#5C5C59] mb-2">
                        Tipologia de Interesse
                      </label>
                      <select
                        data-testid="contact-input-tipologia"
                        value={form.tipologia}
                        onChange={(e) => update("tipologia", e.target.value)}
                        className="w-full bg-transparent border-0 border-b border-[#E5E5DF] focus:border-[#2B3B31] py-3 text-[15px] text-[#1A1A1A] outline-none transition-colors appearance-none cursor-pointer"
                      >
                        <option value="">Selecione a tipologia</option>
                        {TIPOLOGIAS.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.22em] uppercase text-[#5C5C59] mb-2">
                        Bloco
                      </label>
                      <select
                        data-testid="contact-input-bloco"
                        value={form.bloco}
                        onChange={(e) => update("bloco", e.target.value)}
                        className="w-full bg-transparent border-0 border-b border-[#E5E5DF] focus:border-[#2B3B31] py-3 text-[15px] text-[#1A1A1A] outline-none transition-colors appearance-none cursor-pointer"
                      >
                        <option value="">Bloco A, B ou C</option>
                        {BLOCOS.map((b) => (
                          <option key={b.value} value={b.value}>{b.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] tracking-[0.22em] uppercase text-[#5C5C59] mb-2">
                      Mensagem (opcional)
                    </label>
                    <textarea
                      data-testid="contact-input-message"
                      value={form.mensagem}
                      onChange={(e) => update("mensagem", e.target.value)}
                      rows={3}
                      placeholder="Conte-nos o que procura"
                      className="w-full bg-transparent border-0 border-b border-[#E5E5DF] focus:border-[#2B3B31] py-3 text-[15px] text-[#1A1A1A] outline-none transition-colors resize-none"
                    />
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                    <p className="text-[11px] text-[#9E9789] leading-relaxed max-w-xs">
                      Dados tratados de forma confidencial. Venda exclusiva ERA Imobiliária & RE/MAX Portugal.
                    </p>
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={loading}
                      className="group inline-flex items-center justify-center gap-3 h-12 lg:h-14 px-7 bg-[#2B3B31] text-[#F9F9F6] text-[12px] tracking-[0.24em] uppercase hover:-translate-y-[2px] disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-300"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          A enviar
                        </>
                      ) : (
                        <>
                          Solicitar Informações
                          <ArrowRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

function Field({ id, label, value, onChange, placeholder, type = "text", testId, required }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[10px] tracking-[0.22em] uppercase text-[#5C5C59] mb-2">
        {label}{required && <span className="text-[#2B3B31]"> *</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        required={required}
        className="w-full bg-transparent border-0 border-b border-[#E5E5DF] focus:border-[#2B3B31] py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#9E9789] outline-none transition-colors"
      />
    </div>
  );
}

export default ContactForm;
